import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy, signal } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogClose,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { EntityType, DataObject } from '@miq/shared-models';
import { AppStorageService } from '@miq/platform';
import { L10nTranslationService, L10nTranslationModule } from 'angular-l10n';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatCheckbox } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { MatDivider } from '@angular/material/divider';
import { NgStyle, UpperCasePipe } from '@angular/common';
import { GoesToRecordComponent } from '../../components/goes-to-record/goes-to-record.component';
import { GetInitialColorPipe } from './initial-color.pipe';

const NICE_CONFIRM_DIALOG_KEY = 'Confirm_Dialog_Who_Remember_Your_Choice';
export interface ConfirmData {
  message?: string;
  multipleMessages?: string[];
  emailList?: string[];
  emailListWithNames?: Array<{ name: string; email: string }>;
  confirmText?: string;
  cancelText?: string;
  leaveMessage?: string;
  swap?: boolean;
  rememberMyChoiceDialogId?: string;
  doNotRemindMeText?: string;
  translate?: boolean;
  duplicateAlert?: {
    existingId?: number;
    entity: EntityType;
  };
  showCloseButton?: boolean;
  title?: string;
  hideIcon?: boolean;
  header?: string;
  output?: DataObject;
  /** Renders an extra checkbox in the dialog; result is returned as { confirmed, checkboxChecked } instead of a plain boolean */
  checkbox?: { label: string; initialChecked?: boolean };
}
export interface ConfirmDialogResult {
  confirmed: boolean;
  checkboxChecked: boolean;
}
export interface NotificationDialogData {
  status?: string;
  entity?: string;
  recordId?: number;
  propertyType?: string;
}
const defaultLeaveMessage = '';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-confirm',
  templateUrl: './confirm-dialog.component.html',
  styleUrl: './confirm-dialog.component.scss',
  imports: [
    MatButton,
    MatDialogClose,
    MatIcon,
    L10nTranslationModule,
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    MatCheckbox,
    FormsModule,
    MatDivider,
    NgStyle,
    GoesToRecordComponent,
    MatDialogActions,
    UpperCasePipe,
    GetInitialColorPipe
],
})
export class ConfirmDialogComponent implements OnInit, OnDestroy {
  readonly showDialog = signal(false);
  public rememberChoice = false;
  public checkboxValue = false;
  readonly showCloseButton = signal(true);
  public output = null;
  protected dialogRef = inject(MatDialogRef<ConfirmDialogComponent>);
  public data = inject<ConfirmData>(MAT_DIALOG_DATA);
  protected translationService = inject(L10nTranslationService);
  protected storage = inject(AppStorageService);
  constructor() {
    const data = this.data;
    if (data.translate) {
      data.message = data.message ? this.translationService.translate(data.message) : undefined;
      data.confirmText = data.confirmText ? this.translationService.translate(data.confirmText) : undefined;
      data.cancelText = data.cancelText ? this.translationService.translate(data.cancelText) : undefined;
      data.leaveMessage = data.leaveMessage ? this.translationService.translate(data.leaveMessage) : undefined;
      data.doNotRemindMeText = data.doNotRemindMeText
        ? this.translationService.translate(data.doNotRemindMeText)
        : undefined;
      data.title = data.title ? this.translationService.translate(data.title) : undefined;
    }
    this.output = data.output || null;
    if (data.rememberMyChoiceDialogId) {
      const choice = this.remindTheirChoice(data.rememberMyChoiceDialogId);
      if (choice !== null) {
        this.selectOption(choice);
        return;
      }
    }
    this.setData(data);
  }
  ngOnInit(): void {
    // required by angular-l10n
  }
  ngOnDestroy(): void {
    // required by angular-l10n
  }


  getFirstName(name?: string): string {
    return name?.split(' ')[0] || '';
  }

  getLastName(name?: string): string {
    const parts = name?.split(' ') || [];
    return parts.length > 1 ? parts[parts.length - 1] : '';
  }

  private remindTheirChoice(dialogId: string): boolean | null {
    const confirmDialogValues = this.storage.getItem(NICE_CONFIRM_DIALOG_KEY) || {};
    const value = confirmDialogValues[dialogId];
    return value != null ? value : null;
  }
  private saveTheirChoice(dialogId: string, choice: boolean) {
    const confirmDialogValues = this.storage.getItem(NICE_CONFIRM_DIALOG_KEY) || {};
    confirmDialogValues[dialogId] = choice;
    this.storage.setItem(NICE_CONFIRM_DIALOG_KEY, confirmDialogValues);
  }
  private setData({
    message = this.translationService.translate('shared.areYouSure'),
    confirmText = this.translationService.translate('shared.sYes'),
    doNotRemindMeText = this.translationService.translate('shared.doNotRemindMeDefaultText'),
    cancelText = null,
    swap = false,
    leaveMessage = defaultLeaveMessage,
    rememberMyChoiceDialogId = null,
    multipleMessages = [],
    duplicateAlert = null,
    showCloseButton = true,
    title = this.translationService.translate('shared.alert'),
    header = null,
    emailListWithNames = [],
    emailList = [],
    checkbox = null
  }) {
    this.data = {
      message,
      confirmText,
      cancelText,
      swap,
      multipleMessages,
      leaveMessage,
      rememberMyChoiceDialogId,
      doNotRemindMeText,
      duplicateAlert,
      title,
      hideIcon: this.data.hideIcon,
      header,
      emailListWithNames,
      emailList,
      checkbox,
    };
    this.checkboxValue = checkbox?.initialChecked ?? false;
    this.showDialog.set(true);
    this.showCloseButton.set(showCloseButton);
  }
  onClose() {
    this.dialogRef.close();
  }
  public selectOption(yesNo: boolean) {
    if (this.data.rememberMyChoiceDialogId != null && this.rememberChoice) {
      this.saveTheirChoice(this.data.rememberMyChoiceDialogId, yesNo);
    }
    if (this.data.checkbox) {
      this.dialogRef.close({ confirmed: yesNo, checkboxChecked: this.checkboxValue });
      return;
    }
    this.dialogRef.close(yesNo ? this.output || yesNo : yesNo);
  }
}
